import React from 'react'
import { XStack, YStack } from 'tamagui'
import { Trans, useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Overview = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Presentations represent talks, workshops, or demos given at{' '}
            <Link href='/docs/events/overview'>Events</Link>. They give your community's
            content a permanent home so anyone can revisit a talk long after the
            Event has ended.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/presentations/presentations-listing.png'
          alt='Explore Presentations listing page showing Presentation cards with titles, presenter names, and associated Events'
          caption={t`The Presentations listing page where visitors can browse talks across all Guilds`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='what-presentations-include' title={t`What Presentations Include`}>
          <Paragraph size='$5'>
            <Trans>
              Each Presentation has a title, description, presenter attribution,
              and an optional video embed from YouTube or Vimeo. Presentations can
              be linked to the{' '}
              <Link href='/docs/events/overview'>Events</Link> where they were delivered
              and to the{' '}
              <Link href='/docs/guilds/overview'>Guilds</Link> that hosted them.
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='edit' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Title and description</Paragraph> — Give
                  your talk a clear name and a summary of what it covers.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='person' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Presenter attribution</Paragraph> — Link
                  the Presentation to an existing user or an external speaker identified by
                  name and email.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='play' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Video embed</Paragraph> — Optionally
                  attach a YouTube or Vimeo recording so viewers can watch directly on the
                  Presentation page.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='link' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Event and Guild links</Paragraph> — Connect
                  the Presentation to the Events where it was delivered and the Guilds that
                  hosted it.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='external-presenters' title={t`External Presenters`}>
          <Paragraph size='$5'>
            <Trans>
              Presenters can be existing users on Guild or external speakers
              identified by name and email. When a Presentation is created for an
              external speaker, they receive an invitation to claim it and add it
              to their own profile. See{' '}
              <Link href='/docs/presentations/claiming'>Claiming a Presentation</Link> for
              details.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Overview
